import { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Label,
  Badge,
} from "reactstrap";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { BsTrash3 } from "react-icons/bs";
import DefaultButton from "../../../../../components/Buttons/Default";
import { useSettings } from "../../../../../context/settings";
import { WEEK_DAYS, shiftMeta } from "../../../utils/shifts";

const DEFAULT_START = "09:00";
const DEFAULT_END = "17:00";

const buildDefaultDays = () =>
  WEEK_DAYS.map((day) => ({ day, enabled: true, start: DEFAULT_START, end: DEFAULT_END }));

const buildLoopDays = () =>
  Array.from({ length: 3 }, () => ({ off: false, start: DEFAULT_START, end: DEFAULT_END }));

// Accepts both the legacy loop shape (array of { start, end }) and the
// current shape ({ anchor, days }).
const normalizeLoop = (loop) => {
  if (Array.isArray(loop)) {
    return { anchor: "", days: loop.map((d) => ({ off: false, start: d.start, end: d.end })) };
  }
  if (loop?.days?.length) {
    return { anchor: loop.anchor ? new Date(loop.anchor) : "", days: loop.days };
  }
  return { anchor: "", days: buildLoopDays() };
};

function ShiftHoursModal({ isOpen, toggle, value, onSave }) {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const [activeTab, setActiveTab] = useState("default");
  const [defaultDays, setDefaultDays] = useState(buildDefaultDays);
  const [loopAnchor, setLoopAnchor] = useState("");
  const [loopDays, setLoopDays] = useState(buildLoopDays);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(value?.mode || "default");
    setDefaultDays(value?.default?.length ? value.default : buildDefaultDays());
    const loop = normalizeLoop(value?.loop);
    setLoopAnchor(loop.anchor);
    setLoopDays(loop.days);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDefaultDay = (index, patch) =>
    setDefaultDays((days) => days.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const updateLoopDay = (index, patch) =>
    setLoopDays((days) => days.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const addLoopDay = () =>
    setLoopDays((days) => [...days, { off: false, start: DEFAULT_START, end: DEFAULT_END }]);

  const removeLoopDay = (index) =>
    setLoopDays((days) => days.filter((_, i) => i !== index));

  const handleSave = () => {
    onSave?.({
      mode: activeTab,
      default: defaultDays,
      loop: { anchor: loopAnchor || null, days: loopDays },
    });
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>{t("hr.shiftHours")}</ModalHeader>

      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink
              active={activeTab === "default"}
              onClick={() => setActiveTab("default")}
              style={{ cursor: "pointer" }}
            >
              {t("hr.default")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={activeTab === "loop"}
              onClick={() => setActiveTab("loop")}
              style={{ cursor: "pointer" }}
            >
              {t("hr.loop")}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="pt-3">
          <TabPane tabId="default">
            {defaultDays.map((d, i) => (
              <div
                key={d.day}
                className={`d-flex align-items-center gap-2 py-2 ${
                  i < defaultDays.length - 1 ? "border-bottom" : ""
                }`}
              >
                <div className="form-check form-switch mb-0" style={{ minWidth: 120 }}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`shift-day-${d.day}`}
                    checked={d.enabled}
                    onChange={() => updateDefaultDay(i, { enabled: !d.enabled })}
                  />
                  <label className="form-check-label fw-medium" htmlFor={`shift-day-${d.day}`}>
                    {t(`hr.${d.day}`)}
                  </label>
                </div>
                <Input
                  type="time"
                  bsSize="sm"
                  value={d.start}
                  disabled={!d.enabled}
                  onChange={(e) => updateDefaultDay(i, { start: e.target.value })}
                />
                <span className="text-muted">-</span>
                <Input
                  type="time"
                  bsSize="sm"
                  value={d.end}
                  disabled={!d.enabled}
                  onChange={(e) => updateDefaultDay(i, { end: e.target.value })}
                />
              </div>
            ))}
          </TabPane>

          <TabPane tabId="loop">
            <div className="mb-3">
              <Label className="fw-medium mb-1">{t("hr.cycleStart")}</Label>
              <DatePicker
                selected={loopAnchor || null}
                onChange={(date) => setLoopAnchor(date)}
                className="form-control form-control-sm"
                dateFormat={datePickerFormat}
                placeholderText={datePickerFormat.toLowerCase()}
              />
              <small className="text-muted d-block mt-1">{t("hr.cycleStartHint")}</small>
            </div>

            {loopDays.map((d, i) => {
              const meta = shiftMeta(d.start, d.end);
              return (
                <div
                  key={i}
                  className="d-flex align-items-center gap-2 py-2 border-bottom"
                >
                  <span className="fw-medium text-nowrap" style={{ minWidth: 58 }}>
                    {t("hr.day")} {i + 1}
                  </span>
                  <div className="form-check form-switch mb-0" style={{ minWidth: 34 }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      aria-label={t("hr.restDay")}
                      checked={!d.off}
                      onChange={() => updateLoopDay(i, { off: !d.off })}
                    />
                  </div>

                  {d.off ? (
                    <span className="text-muted fst-italic flex-grow-1">{t("hr.restDay")}</span>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <Input
                        type="time"
                        bsSize="sm"
                        value={d.start}
                        onChange={(e) => updateLoopDay(i, { start: e.target.value })}
                        style={{ maxWidth: 130 }}
                      />
                      <span className="text-muted">-</span>
                      <Input
                        type="time"
                        bsSize="sm"
                        value={d.end}
                        onChange={(e) => updateLoopDay(i, { end: e.target.value })}
                        style={{ maxWidth: 130 }}
                      />
                      {meta.overnight && (
                        <Badge color="warning" pill className="text-dark">
                          {t("hr.nextDay")}
                        </Badge>
                      )}
                      <small className="text-muted text-nowrap">{meta.hours}h</small>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 ms-auto"
                    onClick={() => removeLoopDay(i)}
                    disabled={loopDays.length <= 1}
                    aria-label={t("common.delete")}
                  >
                    <BsTrash3 />
                  </button>
                </div>
              );
            })}

            <div className="d-flex align-items-center justify-content-between mt-3">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addLoopDay}>
                + {t("hr.addDay")}
              </button>
              <small className="text-muted">{t("hr.cycleLength", { count: loopDays.length })}</small>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>

      <ModalFooter>
        <button className="btn btn-secondary btn-sm" onClick={toggle}>
          {t("common.cancel")}
        </button>
        <DefaultButton color="primary" size="sm" onClick={handleSave}>
          {t("common.save")}
        </DefaultButton>
      </ModalFooter>
    </Modal>
  );
}

export default ShiftHoursModal;
